/* eslint-disable max-lines */
import { InlineMessage } from '@strata/react-inline-message';

import { Columns } from 'components/shared/Columns';
import { ContentBox } from 'components/shared/ContentBox';
import { Page } from 'components/shared/Page';
import { Section } from 'components/shared/Section';

const inlineMessages = [
    <p key="1">
        Inline messaging should provide immediate feedback on user input, such as confirming entered
        data or highlighting errors, along with offering guidance or instructions.
    </p>,
    <div key="2">
        <h3>Content</h3>
        <ul>
            <li>Names of applications, pages within apps, and acronyms should be capitalized.</li>
            <li>
                Include hyperlinks or instructions within the message if additional actions are
                needed.
            </li>
            <li>Highlight key information with bold text.</li>
            <li>Highlight user-entered confirmation text in all caps and quotation marks.</li>
            <li>Can include a title to provide context or clarity.</li>
        </ul>
    </div>,
    <div key="3">
        <ContentBox
            content={
                <InlineMessage
                    wrapText
                    intent={InlineMessage.Intent.WARNING}
                    title=""
                    label="This rate is less competitive than the default rate. If intentional, type &#34;CONFIRM&#34; below."
                />
            }
        />
        <div className="sds_text__caption">
            Inline message example of user-entered confirmation text inside of quotation marks.
        </div>
    </div>,
    <div key="4">
        <h3>Inline Message Types</h3>
        <ContentBox
            stackVertical
            content={
                <>
                    <InlineMessage
                        wrapText
                        intent={InlineMessage.Intent.ERROR}
                        title="Error"
                        label="Indicates errors to alert users of issues immediately."
                    />
                    <InlineMessage
                        wrapText
                        intent={InlineMessage.Intent.INFO}
                        title="Informational"
                        label="Offers additional information or context."
                    />
                    <InlineMessage
                        wrapText
                        intent={InlineMessage.Intent.SUCCESS}
                        title="Success"
                        label="Provides positive feedback when an action is completed successfully."
                    />
                    <InlineMessage
                        wrapText
                        intent={InlineMessage.Intent.TIP}
                        title="Tip"
                        label="Provides instructions or helpful suggestions on how or why to use a feature."
                    />
                    <InlineMessage
                        wrapText
                        intent={InlineMessage.Intent.WARNING}
                        title="Warning"
                        label="When user input might be acceptable but is not ideal or recommended."
                    />
                </>
            }
        />
    </div>,
];

const toastMessages = [
    <p key="5">
        Toast messages are used to provide instant feedback in response to user actions providing
        reassurance that it was performed successfully or unsuccessfully.
    </p>,
    <div key="6">
        <h3>Success Messages</h3>
        <h4>Display when</h4>
        <ul>
            <li>The user&#39;s action grants no feedback otherwise.</li>
            <li>An action was successfully performed.</li>
        </ul>
    </div>,
    <div key="7">
        <h4>Formatting and structure</h4>
        <ContentBox
            centerContent
            content={
                <img
                    style={{ width: 377 }}
                    src="https://sseasacdn.blob.core.windows.net/strata-website/messaging-success-message.svg"
                    alt="Example of success toast message formatting structure"
                    loading="lazy"
                />
            }
        />
    </div>,
    <div key="8">
        <h4>More examples of success toast messages</h4>
        <ul>
            <li>Prescriber List deleted successfully.</li>
            <li>Referrals transitioned successfully.</li>
        </ul>
    </div>,
    <div key="9">
        <h3>Error Messages</h3>
        <h4>Display when</h4>
        <ul>
            <li>A user completes an action that failed to execute.</li>
        </ul>
    </div>,
    <div key="10">
        <h4>Formatting and structure</h4>
        <p>Use as many of the elements in the example below as possible.</p>
        <ContentBox
            centerContent
            content={
                <img
                    style={{ width: '100%' }}
                    src="https://sseasacdn.blob.core.windows.net/strata-website/messaging-error-message.png"
                    alt="Example of error toast message formatting structure"
                    loading="lazy"
                />
            }
        />
    </div>,
    <div key="11">
        <h4>More examples of error toast messages</h4>
        <ul>
            <li>Sorry, you were outbid.</li>
            <li>Unable to submit referral please try again.</li>
        </ul>
    </div>,
];

const tooltipMessages = [
    <p key="12">
        Tooltips enhance usability by offering quick, accessible help and guidance to users as they
        navigate through the application. They provide additional information or context to an
        element when the user triggers the message. The message disappears when the user moves away
        from the element.
    </p>,
    <div key="13">
        <h3>Use Cases</h3>
        <ul>
            <li>
                Provide additional information about specific features or icons to reduce confusion.
            </li>
            <li>
                Use tooltips to display the full content of text that is truncated due to space
                constraints.
            </li>
            <li>Explain the functionality of complex features or settings.</li>
        </ul>
    </div>,
    <div key="14">
        <ContentBox
            centerContent
            content={
                <img
                    style={{ width: 397 }}
                    src="https://sseasacdn.blob.core.windows.net/strata-website/messaging-use-case-tooltiip-icon.svg"
                    alt="Example of tooltip being used to explain the meaning of an icon"
                    loading="lazy"
                />
            }
        />
        <div className="sds_text__caption">
            In this example a tooltip is being used to explain the meaning of an icon.
        </div>
    </div>,
    <div key="15">
        <ContentBox
            centerContent
            content={
                <img
                    style={{ width: 471 }}
                    src="https://sseasacdn.blob.core.windows.net/strata-website/messaging-use-case-info.svg"
                    alt="Example of info tooltip being used to provide clarity to a complex topic or feature"
                    loading="lazy"
                />
            }
        />
        <div className="sds_text__caption">
            Info icons can be used to provide more clarity to a complex topic or feature.
        </div>
    </div>,
    <div key="16">
        <ContentBox
            centerContent
            content={
                <img
                    style={{ width: 241 }}
                    src="https://sseasacdn.blob.core.windows.net/strata-website/messaging-use-case-table.svg"
                    alt="Example of tooltip being used to display the full name of a label that is truncated"
                    loading="lazy"
                />
            }
        />
        <div className="sds_text__caption">
            In this example a tooltip is used to display the full name of a label that is truncated.
        </div>
    </div>,
    <div key="17">
        <h3>Tooltip Types</h3>
        <Columns
            columns={Columns.ColumnNumber.TWO}
            content={
                <>
                    <div>
                        <h4>Default tooltip</h4>
                        <p>
                            Use a default tooltip for general clarifications on icons, truncated
                            text, and statuses.
                        </p>
                    </div>
                    <ContentBox
                        centerContent
                        content={
                            <img
                                style={{ width: 119 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/messaging-tooltip-default.svg"
                                alt="Example of default tooltip"
                                loading="lazy"
                            />
                        }
                    />
                    <div>
                        <h4>Error tooltip</h4>
                        <p>
                            Use an error tooltip to provide additional context about issues or
                            problems within the application.
                        </p>
                    </div>
                    <ContentBox
                        centerContent
                        content={
                            <img
                                style={{ width: 231 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/messaging-tooltip-error.svg"
                                alt="Example of error tooltip"
                                loading="lazy"
                            />
                        }
                    />
                    <div>
                        <h4>Informational tooltip</h4>
                        <p>
                            Use an informational tooltip to provide context or clarity on the
                            purpose or function of a feature.
                        </p>
                    </div>
                    <ContentBox
                        centerContent
                        content={
                            <img
                                style={{ width: 231 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/messaging-tooltip-info.svg"
                                alt="Example of informational tooltip"
                                loading="lazy"
                            />
                        }
                    />
                    <div>
                        <h4>Success tooltip</h4>
                        <p>
                            Use a success tooltip to offer further clarification on a success icon
                            or status.
                        </p>
                    </div>
                    <ContentBox
                        centerContent
                        content={
                            <img
                                style={{ width: 152 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/messaging-tooltip-success.svg"
                                alt="Example of success tooltip"
                                loading="lazy"
                            />
                        }
                    />
                    <div>
                        <h4>Warning tooltip</h4>
                        <p>
                            Use a warning tooltip to provide context for a warning status or icon,
                            offering insight into potential risks.
                        </p>
                    </div>
                    <ContentBox
                        centerContent
                        content={
                            <img
                                style={{ width: 156 }}
                                src="https://sseasacdn.blob.core.windows.net/strata-website/messaging-tooltip-warning.svg"
                                alt="Example of warning tooltip"
                                loading="lazy"
                            />
                        }
                    />
                </>
            }
        />
    </div>,
    <div key="18">
        <h3>Multi-line Tooltips</h3>
        <p>A multi-line tooltip is better suited for messages that are longer and more complex.</p>
        <h4>When to use multi-line tooltips</h4>
        <ul>
            <li>Use when displaying a list of items.</li>
            <li>Use when multiple pieces of data needs to be shown for an element.</li>
        </ul>
    </div>,
    <ContentBox
        key="19"
        centerContent
        content={
            <img
                style={{ width: 346 }}
                src="https://sseasacdn.blob.core.windows.net/strata-website/message-multiline-tooltip.svg"
                alt="Example of multi-line tooltip"
                loading="lazy"
            />
        }
    />,
];

export const MessagingGuidelines = () => (
    <div>
        <Page
            pageTitle="Messaging Guidelines"
            pageDescription={
                <p>This section covers guidelines for inline, toast, and tooltip messages.</p>
            }
            pageContent={
                <>
                    <Section title="Inline Messages" contentArray={inlineMessages} />
                    <Section title="Toast Messages" contentArray={toastMessages} />
                    <Section title="Tooltip Messages" contentArray={tooltipMessages} />
                </>
            }
        />
    </div>
);
